import React, { useState, useEffect } from 'react';
import { fetchMFAPreference, updateMFAPreference } from 'aws-amplify/auth';
import MFASetup from './MFASetup';
import './SecuritySettings.css';

const SecuritySettings: React.FC = () => {
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showSetup, setShowSetup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    checkMFAStatus();
  }, []);

  const checkMFAStatus = async () => {
    try {
      setLoading(true);
      const mfaPreference = await fetchMFAPreference();
      const isEnabled = mfaPreference.preferred === 'TOTP' || 
                       (mfaPreference.enabled && mfaPreference.enabled.includes('TOTP'));
      setMfaEnabled(isEnabled || false);
    } catch (err: any) {
      console.error('Error checking MFA status:', err);
      setError('Failed to load MFA status');
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    if (!window.confirm('Are you sure you want to disable two-factor authentication? This will make your account less secure.')) {
      return;
    }

    try {
      setLoading(true);
      setError('');
      await updateMFAPreference({ totp: 'DISABLED' });
      setMfaEnabled(false);
      alert('Two-factor authentication has been disabled');
    } catch (err: any) {
      console.error('Error disabling MFA:', err);
      setError('Failed to disable MFA. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupComplete = () => {
    setShowSetup(false);
    setMfaEnabled(true);
  };

  if (showSetup) {
    return (
      <div className="security-settings-container">
        <button 
          onClick={() => setShowSetup(false)}
          className="back-to-settings-button"
        >
          ← Back to Security Settings
        </button>
        <MFASetup onComplete={handleSetupComplete} />
      </div>
    );
  }

  return (
    <div className="security-settings-container">
      <button 
        onClick={() => window.history.back()}
        className="back-button"
      >
        ← Back to Dashboard
      </button>
      <h1>Security Settings</h1>
      
      <div className="settings-section">
        <div className="section-header">
          <div>
            <h2>Two-Factor Authentication</h2>
            <p className="section-description">
              Add an extra layer of security to your account by requiring a code from your authenticator app when you sign in.
            </p>
          </div>
          <div className="mfa-status-badge">
            {mfaEnabled ? (
              <span className="status-enabled">Enabled</span>
            ) : (
              <span className="status-disabled">Disabled</span>
            )}
          </div>
        </div>

        {loading ? (
          <div className="loading-container">
            <span className="loading-spinner"></span>
            <p>Loading...</p>
          </div>
        ) : (
          <div className="section-content">
            {mfaEnabled ? (
              <div className="mfa-enabled-content">
                <div className="info-box success">
                  <div className="info-icon">✓</div>
                  <div>
                    <h3>Your account is protected</h3>
                    <p>You'll need to enter a code from your authenticator app each time you sign in.</p>
                  </div>
                </div>
                
                <button 
                  onClick={disableMFA}
                  className="btn btn-danger"
                  disabled={loading}
                >
                  Disable Two-Factor Authentication
                </button>
              </div>
            ) : (
              <div className="mfa-disabled-content">
                <div className="info-box warning">
                  <div className="info-icon">⚠</div>
                  <div>
                    <h3>Your account is not fully protected</h3>
                    <p>Enable two-factor authentication to add an extra layer of security.</p>
                  </div>
                </div>

                <div className="benefits-list">
                  <h3>Benefits of Two-Factor Authentication:</h3>
                  <ul>
                    <li>
                      <span className="benefit-icon">🔒</span>
                      <span>Protects your account even if your password is compromised</span>
                    </li>
                    <li>
                      <span className="benefit-icon">📱</span>
                      <span>Works with popular authenticator apps like Google Authenticator</span>
                    </li>
                    <li>
                      <span className="benefit-icon">⚡</span>
                      <span>Quick and easy to use - just enter a 6-digit code</span>
                    </li>
                    <li>
                      <span className="benefit-icon">🆓</span>
                      <span>No SMS delivery dependency</span>
                    </li>
                  </ul>
                </div>
                
                <button 
                  onClick={() => setShowSetup(true)}
                  className="btn btn-primary"
                >
                  Enable Two-Factor Authentication
                </button>
              </div>
            )}

            {error && <div className="error-message">{error}</div>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
